<template>
  <div>
    <template v-if="isMobile">
      <OrIconButtonV3
        icon="more_vert"
        @click="isOpen = true"
      />
    </template>
    <div
      v-else
      class="flex items-center "
    >
      <OrSortingV3
        v-show="props.measureOptions.length"
        v-model="localMeasureTraffic"
        :options="props.measureOptions"
        class="mr-md"
      />
      <OrSortingV3
        v-show="props.viewOptions.length"
        v-model="localViewTraffic"
        :options="props.viewOptions"
        class="mr-md"
      />
      <OrSortingV3
        v-show="props.groupingOptions.length"
        v-model="localGroupTraffic"
        :options="props.groupingOptions"
      />
    </div>
    <OrBottomSheetV3
      :is-open="isOpen"
      @close="closeBottomSheet"
    >
      <template #header>
        <div
          v-show="headerTitle"
          class="flex items-center"
          @click="goBack"
        >
          <OrIconV3
            icon="arrow_back"
            class="mr-md"
          />
          <span class="grow">{{ headerTitle }}</span>
        </div>
      </template>
      <template v-if="!selectedOptions">
        <div
          v-for="(item, id) in filterList"
          :key="id"
          class="flex items-center py-sm px-lg mb-xs hover:bg-primary-opacity-0-12 dark:hover:bg-primary-opacity-0-12-dark cursor-pointer"
          @click="selectFilter(item.value)"
        >
          <span class="grow mr-sm hover:font-bold">{{ item.label }}</span>
          <OrTagV3
            v-show="item.selectedValue"
            class="mr-sm"
          >
            {{ item.selectedValue }}
          </OrTagV3>
          <OrIconV3 icon="chevron_right" />
        </div>
      </template>
      <template v-else>
        <div
          v-for="(item, optKey) in props[selectedOptions]"
          :key="optKey"
          class="py-sm px-lg mb-xs hover:bg-primary-opacity-0-12 dark:hover:bg-primary-opacity-0-12-dark cursor-pointer"
          :class="[isSelectedOption(item.value) ?'bg-primary-opacity-0-12 dark:bg-primary-opacity-0-12-dark': '']"
          @click="selectOption(item.value)"
        >
          {{ item.label }}
        </div>
      </template>
    </OrBottomSheetV3>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { GroupTraffic } from '@/constants';
import {
  OrSortingV3,
  OrIconButtonV3,
  OrBottomSheetV3,
  OrTagV3,
  OrIconV3,
  useResponsive,
} from '@onereach/ui-components';

import { SortingFilterProps } from '@/types';


const props = withDefaults(defineProps<SortingFilterProps>(), {
  measureTraffic: 'cost',
  viewTraffic: 'daily',
  groupTraffic: GroupTraffic.NO_GROUPING,
  measureOptions: () => ([]),
  viewOptions: () => ([]),
  groupingOptions: () => ([]),
});

const emit = defineEmits([
  'update:measure-traffic',
  'update:view-traffic',
  'update:group-traffic',
]);

const { isMobile } = useResponsive();

const localMeasureTraffic = computed({
  get: () => props.viewTraffic,
  set: (val) => emit('update:measure-traffic', val),
});

const localViewTraffic = computed({
  get: () => props.viewTraffic,
  set: (val) => emit('update:view-traffic', val),
});

const localGroupTraffic = computed({
  get: () => props.groupTraffic,
  set: (val) => emit('update:group-traffic', val),
});

const filterList = computed(() => {
  const list: {
    label: string;
    value: string;
    selectedValue?: string;
  }[] = [];

  if (props.measureOptions.length) list.push({
    label: 'Measerment',
    value: 'measureOptions',
    selectedValue: props.measureOptions.find(el => el.value === localMeasureTraffic.value)?.label,
  });

  if (props.viewOptions.length) list.push({
    label: 'View',
    value: 'viewOptions',
    selectedValue: props.viewOptions.find(el => el.value === localViewTraffic.value)?.label,
  });

  if (props.groupingOptions.length) list.push({
    label: 'Grouping',
    value: 'groupingOptions',
    selectedValue: props.groupingOptions.find(el => el.value === localGroupTraffic.value)?.label,
  });

  return list;
});

const selectedOptions = ref<string | null>(null);

const headerTitle = computed(() => filterList.value.find(el => selectedOptions.value === el.value)?.label);

const isOpen = ref<boolean>(false);

const goBack = () => selectedOptions.value = null;
const selectFilter = (value: string) => selectedOptions.value = value;
const closeBottomSheet = () => {
  isOpen.value = false;
  selectedOptions.value = null;
};
const selectOption = (value) => {
  if (selectedOptions.value === 'measureOptions') localMeasureTraffic.value = value;
  else if (selectedOptions.value === 'viewOptions') localViewTraffic.value = value;
  else if (selectedOptions.value === 'groupingOptions') localGroupTraffic.value = value;
  closeBottomSheet();
};

const isSelectedOption = (value) => {
  let selected = false;
  switch (selectedOptions.value) {
    case 'measureOptions':
      selected = Boolean(localMeasureTraffic.value === value);
      break;
    case 'viewOptions':
      selected = Boolean(localViewTraffic.value === value);
      break;
    case 'groupingOptions':
      selected = Boolean(localGroupTraffic.value === value);
      break;
    default:
      selected = false;
  }
  return selected;
};
</script>
